import { StateLevelsService } from './state-levels.service';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  first,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { AssetNotationsService } from './asset-notations.service';
import { BuildingsService } from './buildings.service';
import { ImportsService } from './imports.service';
import { NomenclatureService } from './nomenclature.service';
import { PerimetersService } from './perimeters.service';
import { arrayToIdMap, idMapToArray, ScopeService } from './scope.service';
import { environment } from '../../../environments/environment';
import { UtilsService } from './utils.service';
import { YearsService } from './years.service';
import { QuantityUnitsService } from './quantity-units.service';
import { ChartFiltersService } from './chart-filters.service';
import { AssetListFilter } from '../api/api.service';

interface AssetFilter {
  perimeters?: KPI.Perimeter[];
  assetType?: KPI.AssetType;
}

@Injectable()
export class AssetsService {
  private assetsMap: { [id: number]: KPI.MappedAsset } = {};
  public refresh$: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly LAST_REFRESH_STORAGE_KEY = 'synthesis-last-refresh';

  private getAuditSynthesisLastRefresh(perimeterId: number, year: number): Observable<number> {
    const cacheKey = `${perimeterId}-${year}`;
    try {
      const stored = localStorage.getItem(this.LAST_REFRESH_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return of(data[cacheKey] || 0);
      }
    } catch (e) {
      console.error('Failed to get audit synthesis last refresh', e);
    }
    return of(0);
  }

  private setAuditSynthesisLastRefresh(perimeterId: number, year: number, timestamp: number): void {
    const cacheKey = `${perimeterId}-${year}`;
    try {
      const stored = localStorage.getItem(this.LAST_REFRESH_STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : {};
      data[cacheKey] = timestamp;
      localStorage.setItem(this.LAST_REFRESH_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to set audit synthesis last refresh', e);
    }
  }

  public hasCachedEquipment(_perimeterId: number, _year: number): boolean {
    return false;
  }

  constructor(
    private api: APIService,
    private buildingsService: BuildingsService,
    private importsService: ImportsService,
    private nomenclatureService: NomenclatureService,
    private scope: ScopeService,
    private translate: TranslateService,
    private assetNotationsService: AssetNotationsService,
    private stateLevelsService: StateLevelsService,
    private utils: UtilsService,
    private perimetersService: PerimetersService,
    private yearsService: YearsService,
    private quantityUnitsService: QuantityUnitsService,
    private chartFiltersService: ChartFiltersService,
  ) { }

  /**
   * Gets a single asset by id.
   *
   * @param id The id. of the asset to retrieve.
   */
  public getAsset(id: number): Observable<KPI.MappedAsset> {
    return this.api.assets_assets_get(id).pipe(
      concatMap((rawAsset) => this.mapAssets([rawAsset])),
      map(([mappedAsset]) => mappedAsset),
      tap((mappedAsset) => (this.assetsMap[id] = mappedAsset)),
      catchError(() => of(null)),
    );
  }

  public deleteAsset(id: number): Observable<void> {
    return this.api.assets_asset_delete(id);
  }

  public checkDelete(ids: number[]): Observable<{ can_delete: boolean; has_hypothesis?: boolean; status_label?: string }> {
    return this.api.assets_check_delete(ids);
  }

  // Store lifecycle in memory to avoid multiple calls to the API in same second
  // Can't store it forever because it can change on asset / investment update
  private assetLifecycle: { [key: string]: Observable<KPI.AssetLifecycle> } = {};
  public getAssetLifecycle(id: number): Observable<KPI.AssetLifecycle> {
    const key = id.toString() + Math.floor(Date.now() / 1000).toString();
    if (!this.assetLifecycle[key]) {
      this.assetLifecycle[key] = this.api.get_asset_lifecycle(id).pipe(shareReplay(1));
    }
    return this.assetLifecycle[key];
  }

  public getAssetsEligibleToAutoPPA(assetIds: string): Observable<KPI.MappedMinimalAsset[]> {
    return combineLatest([
      this.perimetersService.perimeters$,
      this.yearsService.endYear$,
      this.chartFiltersService.filter$('theoreticalOrAudit', false),
    ]).pipe(
      switchMap(([perimeters, end_of_life, eol_type]) => {
        var payload: Partial<AssetListFilter> = {
          buildings: PerimetersService.getBuildingsIds(perimeters)?.join(','),
          fields: 'id,label,asset_type,category,super_category,quantity,building,investments,end_year_note,end_of_life,installation_year,theoretical_end_of_life,defaults',
        }
        if (!!assetIds) {
          payload['ids'] = assetIds;
        } else {
          payload['end_of_life'] = end_of_life;
          payload['eol_type'] = eol_type;
          payload['is_eligible_auto_ppa'] = true;
        }
        return this.api.assets_assets(
          payload,
          true,
        ).pipe(
          concatMap((assets) => this.mapMinimalAssets(assets)),
        )
      }
      ),
    );
  }

  /**
   * @deprecated
   * Returns an Observable emitting the assets with the specifieds `ids`.
   * Used to preload the assets in memory for later use, e.g. to link with investments.
   *
   * @param ids A list of assets `id`.
   * @param fields
   */
  public getAssets(ids: number[], fields?: string): Observable<KPI.MappedAsset[]> {
    // Ask to the API only the assets we don't already have in memory
    const cachedIds = Object.keys(this.assetsMap).map(Number);
    const idsToRetrieve = R.difference(ids, cachedIds);

    if (idsToRetrieve.length > 0) {
      return this.api.assets_assets({ ids: idsToRetrieve.join(','), ...(fields ? { fields } : {}) }, true).pipe(
        concatMap((assets) => this.mapAssets(assets)),
        tap((mappedAssets) => mappedAssets.forEach((mappedAsset) => this.assetsMap[mappedAsset.id] = mappedAsset)),
        map(() => ids.map((id) => this.assetsMap[id])),
      );
      // We already have every assets in memory 👏
    } else {
      const assetsToEmit = ids.map((id) => this.assetsMap[id]);
      return of(assetsToEmit);
    }
  }

  getSiteAsset(siteId: number, queryParams: {[key: string]: string | number | boolean} = null) {
    return this.api.assets_by_site(
      siteId, [
        'id', 'label', 'asset_type', 'quantity', 'building', 'end_of_life',
        'theoretical_end_of_life', 'perimeter', 'installation_year', 'duration_deviation',
        'investments'
      ] as const, queryParams
    );
  }

  getEquipmentFromSynthesis(perimeterId: number, year: number, lastRefresh: number = 0, forceRefresh: boolean = false): Observable<KPI.MappedAsset[]> {
    return this.getAuditSynthesisLastRefresh(perimeterId, year).pipe(
      switchMap((storedLastRefresh: number) => {
        const currentTimestamp: number = Math.round(+moment() / 1000);
        const effectiveLastRefresh = forceRefresh ? lastRefresh : storedLastRefresh;

        return this.api.audit_synthesis(perimeterId, year, effectiveLastRefresh).pipe(
          tap(() => {
            this.setAuditSynthesisLastRefresh(perimeterId, year, currentTimestamp);
          }),
      map((synthesis: any) => {
        if (!synthesis || !synthesis.assets || synthesis.assets.length === 0) {
          return [];
        }

        const allAssets = synthesis.assets;

        const filteredAssets = allAssets.filter((asset: any) => {
          const isParent = asset.parent === null || asset.parent === undefined;
          const isActive = asset.step === KPI.ASSET_STEP.ACTIVE;
          return isParent && isActive;
        });

        filteredAssets.forEach((asset: any) => {
          if (asset.building && typeof asset.building === 'object') {
            asset.building_object = asset.building;
            asset.building = asset.building.id || asset.building;
          }

          if (asset.level !== undefined) {
            (asset as any).level = asset.level;
          }
          if (asset.technical_state_changed_on) {
            (asset as any).technical_state_changed_on = asset.technical_state_changed_on;
          }

          if (asset.asset_type && typeof asset.asset_type === 'object') {
            (asset as any).asset_type_object = asset.asset_type;
            asset.asset_type = asset.asset_type.id || asset.asset_type;
            if ((asset as any).asset_type_object.quantity_unit) {
              (asset as any).quantity_unit = (asset as any).asset_type_object.quantity_unit;
            }
          }

          if (asset.category && typeof asset.category === 'object') {
            (asset as any).category = asset.category;
          }

          if (asset.sub_category && typeof asset.sub_category === 'object') {
            (asset as any).sub_category = asset.sub_category;
          }

          if (asset.lastAccess || asset.last_access) {
            const lastAccessValue = asset.lastAccess || asset.last_access;
            asset.lastAccess = lastAccessValue instanceof Date
              ? lastAccessValue
              : new Date(lastAccessValue);
          }

          if (!asset.asset_notations && asset.asset_notation) {
            asset.asset_notations = Array.isArray(asset.asset_notation)
              ? asset.asset_notation
              : [asset.asset_notation];
          }
        });

        filteredAssets.sort((a: any, b: any) => {
          const aLastAccess = a.lastAccess || a.last_access;
          const bLastAccess = b.lastAccess || b.last_access;

          if (!aLastAccess && !bLastAccess) {
            return b.id - a.id;
          }
          if (!aLastAccess) {
            return 1;
          }
          if (!bLastAccess) {
            return -1;
          }

          const dateA = aLastAccess instanceof Date ? aLastAccess : new Date(aLastAccess);
          const dateB = bLastAccess instanceof Date ? bLastAccess : new Date(bLastAccess);
          const timeDiff = dateB.getTime() - dateA.getTime();

          return timeDiff !== 0 ? timeDiff : b.id - a.id;
        });

        return filteredAssets;
      }),
      switchMap((assets: KPI.Asset[]) => {
        if (assets.length === 0) {
          return of([]);
        }
        const allAssetsToMap: KPI.Asset[] = [...assets];
        assets.forEach((asset: any) => {
          if ((asset as any).children && Array.isArray((asset as any).children) && (asset as any).children.length > 0) {
            allAssetsToMap.push(...(asset as any).children);
          }
        });

        return this.mapAssets(allAssetsToMap).pipe(
          map((mappedAllAssets: KPI.MappedAsset[]) => {
            const mappedAssetsMap = new Map<number, KPI.MappedAsset>();
            mappedAllAssets.forEach(mappedAsset => {
              mappedAssetsMap.set(mappedAsset.id, mappedAsset);
            });

            return assets.map((asset: any) => {
              const mappedParent = mappedAssetsMap.get(asset.id);
              if (!mappedParent) {
                return null;
              }

              if (asset.children && Array.isArray(asset.children) && asset.children.length > 0) {
                const mappedChildren = asset.children
                  .map((child: any) => mappedAssetsMap.get(child.id))
                  .filter((child: KPI.MappedAsset | undefined): child is KPI.MappedAsset => child !== undefined);

                return {
                  ...mappedParent,
                  children: mappedChildren
                } as KPI.MappedAsset;
              }

              return mappedParent;
            }).filter((asset): asset is KPI.MappedAsset => asset !== null);
          })
        );
      })
        );
      })
    );
  }

  public getAssetsPaginated(filter: AssetFilter, page: number, fields?: string): Observable<any> {
    const query = {
      ...(filter.perimeters
        ? {
          buildings: PerimetersService.getBuildingsIds(
            filter.perimeters,
          ).join(','),
        }
        : {}),
      ...(filter.assetType ? { asset_type: filter.assetType.id } : {}),
    };

    return this.api.assets_assets_by_page(page, query, fields).pipe(
      map((res: any) => ({
        ...res,
        results: res.results.concat(...res.results.map(r => r.children)),
      })),
      switchMap((res) => combineLatest([
        this.mapAssets(res['results']),
        of(res['count']),
      ])),
      tap(([mappedAssets, _count]) => {
        mappedAssets.forEach(
          (mappedAsset) => this.assetsMap[mappedAsset.id] = mappedAsset,
        );
      }),
    );
  }

  public getAssetsPictures(assetId: number[]): Observable<KPI.AssetPicture[]> {
    return this.api.assets_assets_pictures({ assets: assetId.join(',') });
  }

  public createAssetPicture(formData: FormData): Observable<KPI.AssetPicture> {
    return this.api.assets_create_picture(formData);
  }

  public deleteAssetPicture(id: number): Observable<void> {
    return this.api.assets_delete_picture(id);
  }

  private mapAssets(assets: KPI.Asset[]): Observable<KPI.MappedAsset[]> {
    return combineLatest(
      this.nomenclatureService.assetTypes$,
      this.scope.zones$,
      this.scope.ownershipTypes$,
      this.scope.perimeters$.pipe(idMapToArray()), // Use scope perimeters to work with all perimeters available
      this.importsService.assetImportableInvestmentStatuses$,
      this.scope.assetFloors$,
      this.scope.superCategories$,
      this.scope.categories$,
      this.scope.subCategories$,
      this.scope.auditQuestions$.pipe(first(), idMapToArray()), // For extracting last_notation from asset_notations
    ).pipe(
      first(),
      map(([assetTypes, zones, ownershipTypes, perimeters,
        assetImportableInvestmentStatuses, floors, superCategories, categories, subCategories, auditQuestions,
      ]) => {
        return assets.map((asset) => {
          let last_notation: KPI.MappedAssetNotation | undefined = undefined;
          if (asset.asset_notations && asset.asset_notations.length > 0) {
            const mappedNotations = asset.asset_notations.map((n) => {
              let auditQuestion: KPI.AuditQuestion;
              let auditQuestionItem: KPI.AuditQuestionItem;
              for (auditQuestion of auditQuestions) {
                auditQuestionItem = auditQuestion.items.find((item) => item.id === n.question_item);
                if (auditQuestionItem) {
                  break;
                }
              }
              return {
                ...n,
                audit_question_object: auditQuestion,
                audit_question_item_object: auditQuestionItem,
              } as KPI.MappedAssetNotation;
            });

            const technicalStateNotations = mappedNotations
              .filter((n) => n.audit_question_object?.mnemonic === 'technical_state')
              .sort((a, b) => a.audit_year - b.audit_year);
            last_notation = technicalStateNotations[technicalStateNotations.length - 1];
          }

          let assetPerimeters = this.findAssetPerimeters(asset, perimeters);
          if (assetPerimeters.length === 0 && asset.perimeter) {
            const perimeterId = typeof asset.perimeter === 'object' ? asset.perimeter.id : asset.perimeter;
            const perimeterObj = perimeters.find(p => p.id === perimeterId);
            if (perimeterObj) {
              assetPerimeters = [perimeterObj];
            }
          }

          if (assetPerimeters.length === 0 && (asset as any).building_object) {
            const buildingObj = (asset as any).building_object;
            if (buildingObj.monosite_perimeter) {
              const monositePerimeterId = typeof buildingObj.monosite_perimeter === 'object'
                ? buildingObj.monosite_perimeter.id
                : buildingObj.monosite_perimeter;
              const monositePerimeterObj = perimeters.find(p => p.id === monositePerimeterId);
              if (monositePerimeterObj) {
                assetPerimeters = [monositePerimeterObj];
              }
            }
          }

          const synthesisAssetTypeObject = (asset as any).asset_type_object;
          const assetTypeId = typeof asset.asset_type === 'object' ? (asset.asset_type as any).id : asset.asset_type;
          const assetType = assetTypes.find((a) => a.id === assetTypeId);

          let finalAssetTypeObject = assetType;
          if (synthesisAssetTypeObject && (synthesisAssetTypeObject as any).keywords && Array.isArray((synthesisAssetTypeObject as any).keywords) && (synthesisAssetTypeObject as any).keywords.length > 0) {
            finalAssetTypeObject = assetType ? {
              ...assetType,
              keywords: (synthesisAssetTypeObject as any).keywords
            } : synthesisAssetTypeObject;
          } else if (synthesisAssetTypeObject && !assetType) {
            finalAssetTypeObject = synthesisAssetTypeObject;
          }

          if (!finalAssetTypeObject) {
            return {
              ...asset,
              floor_object: floors[asset.floor],
              zone_object: zones[asset.zone],
              asset_type_object: null,
              super_category: null,
              category: null,
              category_simple: null,
              sub_category: null,
              ownership_type_object: ownershipTypes[asset.ownership_type],
              perimeters: assetPerimeters,
              imported_at_str: asset.imported_at ? new Date(asset.imported_at).toLocaleDateString() : null,
              import_statuses_str: null,
              site_name: assetPerimeters.length > 0 ? assetPerimeters[0].name : '',
              perimeters_names_list: assetPerimeters.slice(1).map((perimeter) => perimeter.name).join(' > '),
              last_notation,
            } as KPI.MappedAsset;
          }

          const sub_category_object = finalAssetTypeObject.parent ? subCategories[finalAssetTypeObject.parent] : null;
          const category_object = sub_category_object?.parent ? categories[sub_category_object.parent] : null;
          const super_category_object = category_object?.parent ? superCategories[category_object.parent] : null;

          let import_statuses_str = null;

          if (asset.import_statuses) {
            const importStatuses = asset.import_statuses.map((status) => {
              return assetImportableInvestmentStatuses.find((stat) => stat.id === status)?.name;
            }).filter(Boolean);
            import_statuses_str = importStatuses.join(', ');
          }

          return {
            ...asset,
            floor_object: floors[asset.floor],
            zone_object: zones[asset.zone],
            asset_type_object: finalAssetTypeObject,
            super_category: super_category_object,
            category: category_object,
            category_simple: category_object ? { id: category_object.id, name: category_object.name } : null,
            sub_category: sub_category_object,
            ownership_type_object: ownershipTypes[asset.ownership_type],
            perimeters: assetPerimeters,
            // FIXME - CH - 2020-03-16 - imported_at has been removed due to the handsome table event spam issue and
            //  workaround
            // see https://dev.azure.com/kpi-intelligence/KPI%20Intelligence/_workitems/edit/1830
            imported_at_str: asset.imported_at ? new Date(asset.imported_at).toLocaleDateString() : null,
            import_statuses_str,
            site_name: assetPerimeters.length > 0 ? assetPerimeters[0].name : '',
            // Remove first perimeter as it's the site_name
            perimeters_names_list: assetPerimeters.slice(1).map((perimeter) => perimeter.name).join(' > '),
            last_notation, // Add last_notation to mapped asset
            // Preserve synthesis-specific fields
            ...((asset as any).level !== undefined ? { level: (asset as any).level } : {}),
            ...((asset as any).technical_state_changed_on ? { technical_state_changed_on: (asset as any).technical_state_changed_on } : {}),
            ...((asset as any).building_object ? { building_object: (asset as any).building_object } : {}),
            ...((asset as any).quantity_unit ? { quantity_unit: (asset as any).quantity_unit } : {}),
            // Cast budget reference prices
            ...(asset.budget_reference_data ? {
              budget_reference_data: {
                ...asset.budget_reference_data,
                budget_price: Number(asset.budget_reference_data.budget_price),
                material_price: Number(asset.budget_reference_data.material_price),
                additional_price: Number(asset.budget_reference_data.additional_price),
                workloads: asset.budget_reference_data.workloads.map((workload) => ({
                  ...workload,
                  hourly_rate: Number(workload.hourly_rate),
                  hours_number: Number(workload.hours_number),
                })),
              },
            } : {}),
          };
        });
      }),
    );
  }

  private mapMinimalAssets(assets: KPI.Asset[]): Observable<KPI.MappedMinimalAsset[]> {
    return combineLatest([
      this.scope.perimeters$.pipe(idMapToArray()),
      this.scope.investmentStatuses$,
      this.assetNotationsService.getLastAssetsEvaluation(assets),
      this.getAssetsPictures(assets.map((a) => a.id)),
      this.scope.superCategories$,
      this.scope.categories$,
      this.scope.subCategories$,
      this.scope.assetTypes$,
      this.scope.investmentTypes$,
      this.quantityUnitsService.quantityUnits$.pipe(arrayToIdMap()),
    ]).pipe(
      first(),
      map(([
        perimeters, investmentStatuses, lastEvaluations, assetPictures, superCategories,
        categories, subCategories, assetTypes, investmentTypes, quantityUnits
      ]) => {
        return assets.map((asset) => {
          const assetPerimeters = this.findAssetPerimeters(asset, perimeters);
          const last_evaluation = lastEvaluations[asset.id];
          const pictures = assetPictures.filter((picture) => picture.asset === asset.id);
          const hypothesis_replacement = asset.investments?.find((i) =>
            investmentStatuses[i.status].hypothesis
            && investmentTypes[i.investment_type].replacement
          )
          if (hypothesis_replacement) {
            hypothesis_replacement.status_object = investmentStatuses[hypothesis_replacement.status]
          }
          const quantity_unit: KPI.QuantityUnit = quantityUnits[assetTypes[asset.asset_type].quantity_unit];
          const asset_type_object = assetTypes[asset.asset_type];
          const sub_category_object = subCategories[asset_type_object.parent];
          const category_object = categories[sub_category_object.parent];
          const super_category_object = superCategories[category_object.parent];

          return {
            id: asset.id,
            label: asset.label,
            perimeter: assetPerimeters.find((p) => !p.is_monosite),
            site: assetPerimeters.find((p) => p.is_monosite),
            pictures,
            last_evaluation,
            super_category: super_category_object?.id,
            category: category_object?.id,
            asset_type: asset.asset_type,
            asset_type_object,
            quantity_unit,
            is_quantity_consistent: !quantity_unit
              || (
                quantity_unit.minimal_quantity <= asset.quantity
                && asset.quantity <= quantity_unit.maximal_quantity
              ),
            eligible_automatic_multi_year_plan: asset.eligible_automatic_multi_year_plan,
            sub_category_object,
            planned_investments: asset.investments?.filter((i) =>
              !investmentStatuses[i.status].hypothesis
              && !investmentStatuses[i.status].is_cancelled
              && !investmentStatuses[i.status].is_closed
            ),
            hypothesis_investments: asset.investments?.filter((i) => investmentStatuses[i.status].hypothesis),
            hypothesis_replacement,
            technical_state_string: last_evaluation
              ? this.stateLevelsService.getTechnicalStateLabelFromLevel(last_evaluation.note)
              : null,
            technical_state_color: last_evaluation
              ? this.stateLevelsService.getColorFromLevel(last_evaluation.note)
              : null,
            quantity: asset.quantity,
            replaced_by: asset.replaced_by,
            end_of_life: asset.end_of_life,
            theoretical_end_of_life: asset.theoretical_end_of_life,
            end_year_note: asset.end_year_note,
            installation_year: asset.installation_year,
            defaults: asset.defaults,
          };
        });
      }),
    );
  }

  private findAssetPerimeters(
    asset: KPI.Asset,
    perimeters: KPI.Perimeter[],
  ): KPI.Perimeter[] {
    return this.buildingsService.findBuildingPerimeters(
      asset.building,
      perimeters,
    );
  }

  /**
   * Updates an asset in the data base
   *
   * @param id
   * @param updatedAsset
   */
  public updateAsset(
    id: number,
    updatedAsset: Partial<KPI.Asset>,
  ): Observable<KPI.Asset> {
    return this.api.assets_assets_patch(id, updatedAsset);
  }

  public getAssetTypeLevelLabel(
    value: number,
  ): string {
    switch (value) {
      case KPI.AssetTypeLevel.NONE:
        return this.translate.instant('pages.insight.assets.levels.none');
      case KPI.AssetTypeLevel.COLLECTION:
        return this.translate.instant('pages.insight.assets.levels.collection');
      case KPI.AssetTypeLevel.UNIT:
        return this.translate.instant('pages.insight.assets.levels.unit');
      case KPI.AssetTypeLevel.COMPONENT:
        return this.translate.instant('pages.insight.assets.levels.component');
      case KPI.AssetTypeLevel.COLLECTION_ITEM:
        return this.translate.instant('pages.insight.assets.levels.collection_item');
      default:
        // should never occur
        return '';
    }
  }

  public openAuditAssetEditor(configuration: { asset: KPI.Asset | KPI.MappedMinimalAsset, siteId: number, tab?: string }) {
    const { asset, siteId, tab } = configuration;
    let popupUrl = `${environment.auditAppURL}/perimeters/${siteId}/asset-detail/${asset.id}`; //?forceSynthesisReload=true`;
    if (tab) {
      popupUrl += `?tab=${encodeURIComponent(tab)}`;
    }
    this.utils.openPopupWindow(popupUrl);
  }
}
